import { HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { EnvironmentProviders, Provider, importProvidersFrom } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgbDateParserFormatter, NgbDatepickerI18n, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { provideEchartsCore } from 'ngx-echarts';
import { NgxFontAwesomeModule } from 'ngx-font-awesome';
import { BearerInterceptorService } from './interceptors/bearer-interceptor.service';
import { ErrorInterceptorService } from './interceptors/error-interceptor.service';
import { UuidInterceptorService } from './interceptors/uuid-interceptor.service';
import { NgbDatePickerTranslateService } from './utils/ngb-date-picker-translate.service';
import { NgbDatePickerAdapter } from './utils/ngbDatePickerAdapter.service';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

/**
 * Creates a TranslateHttpLoader instance.
 *
 * The TranslateHttpLoader is a simple loader for the ngx-translate library.
 * It will load the translations from the given URL (in this case, from the
 * ./assets/i18n directory) and return them as a JSON object.
 *
 * @param http The HttpClient instance that will be used to load the
 * translations.
 * @returns A TranslateHttpLoader instance.
 */
export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

/**
 * Provides the core modules and services for the application.
 *
 * This function returns an array of providers that should be included in the
 * application module. It includes the HTTP interceptors, services, and modules
 * that are necessary for the application to function.
 *
 * @returns An array of providers that should be included in the application
 * module.
 */
export function provideCore(): (Provider | EnvironmentProviders)[] {
    return [
        // Registering interceptors
        { provide: HTTP_INTERCEPTORS, useClass: UuidInterceptorService, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: BearerInterceptorService, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptorService, multi: true },

        // Registering services
        { provide: NgbDateParserFormatter, useClass: NgbDatePickerAdapter },
        { provide: NgbDatepickerI18n, useClass: NgbDatePickerTranslateService },
        DatePipe,

        // Registering modules and components
        provideTranslateService({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            }
        }),
        provideEchartsCore({ echarts: () => import('echarts') }),

        importProvidersFrom(
            NgxFontAwesomeModule,
            CommonModule,
            BrowserAnimationsModule,
            HammerModule,
            NgbModule
        )
    ];
}
